/**
 * ProductFilterBar - Filter chip bar with add-filter dropdown.
 */
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/v2/ui/Button'
import { Badge } from '@/components/v2/ui/Badge'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/v2/ui/DropdownMenu'
import type { ProductTableFilters } from '@/hooks/useProductTableConfig'

type FilterOption = { key: keyof ProductTableFilters; i18nKey: string; defaultValue: boolean | number }

interface ProductFilterBarProps {
  filters: ProductTableFilters
  onFiltersChange: (f: ProductTableFilters) => void
  onReset: () => void
}

export function ProductFilterBar({ filters, onFiltersChange, onReset }: ProductFilterBarProps) {
  const { t } = useTranslation()

  const FILTER_OPTIONS: FilterOption[] = [
    { key: 'is_active',        i18nKey: 'products.filter_status_active',  defaultValue: true },
    { key: 'quantity_30d_min', i18nKey: 'products.filter_quantity_30d',   defaultValue: 1 },
    { key: 'revenue_30d_min',  i18nKey: 'products.filter_revenue_30d',    defaultValue: 1 },
    { key: 'quantity_90d_min', i18nKey: 'products.filter_quantity_90d',   defaultValue: 1 },
    { key: 'revenue_90d_min',  i18nKey: 'products.filter_revenue_90d',    defaultValue: 1 },
  ]

  function fmtValue(key: keyof ProductTableFilters, v: unknown): string {
    if (key === 'is_active') return v ? t('products.filter_val_active') : t('products.filter_val_inactive')
    if (typeof v === 'number' && key.startsWith('revenue'))
      return `\u2265 ${v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}`
    return typeof v === 'number' ? `\u2265 ${v}` : String(v)
  }

  const activeKeys = Object.keys(filters) as (keyof ProductTableFilters)[]
  const available = FILTER_OPTIONS.filter((o) => filters[o.key] === undefined)

  const remove = (key: keyof ProductTableFilters) => {
    const next = { ...filters }
    delete next[key]
    onFiltersChange(next)
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {available.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" iconLeft={<Plus className="h-4 w-4" />}>
              {t('products.filter_add')}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            {available.map((o) => (
              <DropdownMenuItem key={o.key} onSelect={() => onFiltersChange({ ...filters, [o.key]: o.defaultValue })}>
                {t(o.i18nKey)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {activeKeys.map((key) => {
        const opt = FILTER_OPTIONS.find((o) => o.key === key)
        return (
          <Badge key={key} variant="primary" size="sm" onRemove={() => remove(key)}>
            {opt ? t(opt.i18nKey) : key}: {fmtValue(key, filters[key])}
          </Badge>
        )
      })}

      {activeKeys.length > 0 && (
        <button
          type="button"
          onClick={onReset}
          className="text-xs text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 underline"
        >
          {t('products.filter_reset_all')}
        </button>
      )}
    </div>
  )
}
